import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFetchOptions } from "utils/fetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import AiChatIcon from "./AiChatIcon";
import "./AiChatTranscriptPanel.scss";

const COLLAPSED_HEIGHT = 200;

function newId() {
	// Modern browsers
	if (window.crypto && typeof window.crypto.randomUUID === "function") {
		return window.crypto.randomUUID();
	}

	// Fallback (RFC4122-ish). Good enough for client ids in-app.
	const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
	return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

function ChatBubble({ role, html, text, children }) {
	const isUser = role === "user";
	const bubbleClass = isUser ? "ai-chat__bubble ai-chat__bubble--user" : "ai-chat__bubble ai-chat__bubble--assistant";

	return (
		<div className={bubbleClass}>
			<div className="ai-chat__bubble__icon">
				{isUser
					? <FontAwesomeIcon icon={faUser} />
					: <AiChatIcon className="ai-chat__bubble__icon-svg" />
				}
			</div>

			<div className="ai-chat__bubble__content">
				{role === "assistant" ? (
					typeof html === "string" && html.trim().length > 0 ? (
						<div className="ai-chat__bubble__content__text" dangerouslySetInnerHTML={{ __html: html }} />
					) : (
						<span>{text ?? ""}</span>
					)
				) : (
					<span>{text ?? ""}</span>
					)}
				{children}
			</div>
		</div>
	);
}

/**
 * Props:
 * - enabled: whether the AI panel should exist at all
 * - initialQuestion: the "primary" search term to start a new conversation
 * - onStateChange: (state) => void where state is "loading" | "streaming" | "done" | "none"
 */
export default function AiChatTranscriptPanel({ enabled, initialQuestion, onStateChange }) {
	const [conversationId, setConversationId] = useState(null);

	// Full transcript shown in UI.
	const [messages, setMessages] = useState([]); // [{ id, role: 'user'|'assistant', text, html }]

	const [secondaryInput, setSecondaryInput] = useState("");

	const [status, setStatus] = useState("idle"); // idle | streaming | done | error | aborted
	const [error, setError] = useState("");

	// Internal omission flags (match old behaviour: omit entirely)
	const [hasNoAnswer, setHasNoAnswer] = useState(false);
	const [isForbidden, setIsForbidden] = useState(false);

	// Show more / show less
	const [isExpanded, setIsExpanded] = useState(false);
	const [contentHeight, setContentHeight] = useState(0);

	// Tracks whether the first assistant response has ever been received in this conversation.
	const hasReceivedFirstResponse = useRef(false);

	const abortControllerRef = useRef(null);
	const formRef = useRef(null);
	const panelRef = useRef(null);
	const contentRef = useRef(null);

	// To avoid stale reads inside async loops
	const statusRef = useRef(status);
	const hasNoAnswerRef = useRef(hasNoAnswer);
	const isForbiddenRef = useRef(isForbidden);

	useEffect(() => {
		statusRef.current = status;
	}, [status]);

	useEffect(() => {
		hasNoAnswerRef.current = hasNoAnswer;
	}, [hasNoAnswer]);

	useEffect(() => {
		isForbiddenRef.current = isForbidden;
	}, [isForbidden]);

	// Secondary input appears after the first assistant response begins.
	const hasAssistantStarted = useMemo(() => {
		return messages.some((m) => m.role === "assistant" && typeof m.html === "string" && m.html.trim().length > 0);
	}, [messages]);

	const hasAnyAssistantHtml = useMemo(() => {
		return messages.some((m) => m.role === "assistant" && typeof m.html === "string" && m.html.trim().length > 0);
	}, [messages]);

	// Measure content height whenever messages or status change
	useEffect(() => {
		if (contentRef.current) {
			setContentHeight(contentRef.current.scrollHeight);
		}
	}, [messages, status]);

	const needsExpander = contentHeight > COLLAPSED_HEIGHT;

	const stop = () => {
		if (abortControllerRef.current !== null) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
			setStatus("aborted");
		}
	};

	// Keep behaviour consistent with your old panel: abort on navigation away.
	useEffect(() => {
		const handlePageHide = () => stop();
		window.addEventListener("pagehide", handlePageHide);

		return () => {
			window.removeEventListener("pagehide", handlePageHide);
			handlePageHide();
		};
	}, []);

	const hardResetConversation = () => {
		stop();
		setConversationId(null);
		setMessages([]);
		setSecondaryInput("");
		setStatus("idle");
		setError("");
		setHasNoAnswer(false);
		setIsForbidden(false);
		setIsExpanded(false);
		setContentHeight(0);
		hasReceivedFirstResponse.current = false;
	};

	const appendUserMessage = (text) => {
		const id = newId();
		setMessages((prev) => [...prev, { id, role: "user", text, html: null }]);
	};

	const createAssistantPlaceholder = () => {
		const id = newId();
		setMessages((prev) => [...prev, { id, role: "assistant", text: "", html: "" }]);
		return id;
	};

	const appendAssistantHtml = (assistantMessageId, htmlChunk) => {
		setMessages((prev) =>
			prev.map((m) => {
				if (m.id !== assistantMessageId) {
					return m;
				}

				const nextHtml = (m.html ?? "") + (htmlChunk ?? "");
				return { ...m, html: nextHtml };
			})
		);
	};

	/**
	 * Map internal state to parent slot state.
	 * Parent expects: "loading" | "streaming" | "done" | "none"
	 */
	useEffect(() => {
		if (!onStateChange) {
			return;
		}

		if (!enabled) {
			onStateChange("none");
			return;
		}

		// Omit entirely for forbidden or no-answer.
		if (isForbidden || hasNoAnswer) {
			onStateChange("none");
			return;
		}

		// While we wait for first meaningful content, show skeleton.
		if (status === "idle") {
			// But only if we actually have an initial question to run.
			const trimmed = (initialQuestion ?? "").toString().trim();
			if (trimmed.length > 0) {
				onStateChange("loading");
			} else {
				onStateChange("none");
			}
			return;
		}

		if (status === "streaming") {
			onStateChange("streaming");
			return;
		}

		if (status === "done") {
			// If done but nothing to show and no error, omit entirely.
			if (!hasAnyAssistantHtml && !error) {
				onStateChange("none");
				return;
			}

			onStateChange("done");
			return;
		}

		if (status === "error") {
			// Keep panel visible to show error.
			onStateChange("done");
			return;
		}

		if (status === "aborted") {
			onStateChange("none");
		}
	}, [enabled, status, isForbidden, hasNoAnswer, hasAnyAssistantHtml, error, onStateChange, initialQuestion]);

	const startStream = async ({ conversationId: convId, message }) => {
		// Stop any in-flight stream before starting a new one.
		if (abortControllerRef.current !== null) {
			stop();
		}

		// Only reset to "idle" for the very first request in a conversation.
		// For follow-up messages, stay on "streaming" so the skeleton doesn't reappear.
		if (!hasReceivedFirstResponse.current) {
			setStatus("idle");
		}

		setError("");
		setHasNoAnswer(false);
		setIsForbidden(false);

		const ac = new AbortController();
		abortControllerRef.current = ac;

		const clientMessageId = newId();

		const baseOptions = getFetchOptions({
			method: "POST",
			body: {
				conversationId: convId,
				message,
				clientMessageId
			}
		});

		const options = {
			...baseOptions,
			signal: ac.signal
		};

		try {
			const resp = await fetch("/api/v3/chat/stream", options);

			if (resp.status === 409) {
				setStatus("error");
				setError("Duplicate message detected (already sent).");
				abortControllerRef.current = null;
				return;
			}

			if (resp.status === 403) {
				// Omit entirely (aligns with previous panel behaviour).
				setIsForbidden(true);
				setStatus("done");
				abortControllerRef.current = null;
				return;
			}

			if (!resp.ok || !resp.body) {
				setStatus("error");
				setError("Request failed.");
				abortControllerRef.current = null;
				return;
			}

			// Create the assistant bubble now, and stream into it.
			const assistantId = createAssistantPlaceholder();

			const reader = resp.body.getReader();
			const decoder = new TextDecoder("utf-8");
			let buffer = "";

			while (true) {
				const { value, done } = await reader.read();

				if (done) {
					break;
				}

				buffer += decoder.decode(value, { stream: true });

				const lines = buffer.split("\n");
				buffer = lines.pop() ?? "";

				for (const line of lines) {
					const trimmedLine = line.trim();
					if (!trimmedLine) {
						continue;
					}

					let chatEvent;
					try {
						chatEvent = JSON.parse(trimmedLine);
					} 
					catch {
						setStatus("error");
						setError("Failed to parse NDJSON event.");
						stop();
						return;
					}

					const kind = chatEvent.Kind;

					// Optional informational/debug events
					if (kind === "ConversationInfo" || kind === "ConversationStarted") {
						continue;
					}

					if (kind === "RunStarted" || kind === "PhaseChanged") {
						continue;
					}

					if (kind === "NoAnswer") {
						// Omit AI section entirely (your spec).
						setMessages([]);
						setHasNoAnswer(true);

						// Stop stream early to save bandwidth/tokens.
						stop();
						return;
					}

					if (kind === "HtmlChunk") {
						// Flip into "streaming" exactly once.
						if (statusRef.current !== "streaming") {
							setStatus("streaming");
						}

						// Mark that we've received at least one response in this conversation.
						hasReceivedFirstResponse.current = true;

						const html = chatEvent.html ?? chatEvent.Html ?? "";
						if (typeof html === "string" && html.length > 0) {
							appendAssistantHtml(assistantId, html);
						}
						continue;
					}

					if (kind === "Error") {
						setStatus("error");
						setError(chatEvent.message ?? chatEvent.Message ?? "Unknown error.");
						abortControllerRef.current = null;
						stop();
						return;
					}

					if (kind === "Done") {
						setStatus("done");
						abortControllerRef.current = null;

						try {
							await reader.cancel();
						} 
						catch {
							// ignore
						}

						return;
					}

					// Ignore any new event kinds.
				}
			}

			// Stream ended without explicit Done - treat as done.
			setStatus("done");
			abortControllerRef.current = null;
		} 
		catch (err) {
			if (err && err.name === "AbortError") {
				setStatus("aborted");
			} else {
				setStatus("error");
				setError("Request failed.");
			}
			abortControllerRef.current = null;
		}
	};

	/**
	 * Auto-start a NEW conversation whenever initialQuestion changes.
	 * This is how we integrate with SearchResults: the primary search term starts the chat.
	 */
	useEffect(() => {
		if (!enabled) {
			hardResetConversation();
			return;
		}

		const trimmed = (initialQuestion ?? "").toString().trim();

		if (!trimmed) {
			hardResetConversation();
			return;
		}

		// Start over for each new search term.
		hardResetConversation();

		const convId = newId();
		setConversationId(convId);

		appendUserMessage(trimmed);

		startStream({ conversationId: convId, message: trimmed });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, initialQuestion]);

	const handleSecondarySubmit = async (e) => {
		e.preventDefault();

		if (!enabled) {
			return;
		}

		if (!conversationId) {
			return;
		}

		const trimmed = (secondaryInput ?? "").toString().trim();
		if (!trimmed) {
			return;
		}

		appendUserMessage(trimmed);
		setSecondaryInput("");

		await startStream({ conversationId, message: trimmed });
	};

	const handleTextareaKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			formRef.current?.requestSubmit();
		}
	};

	const handleToggle = () => {
		const willCollapse = isExpanded;
		setIsExpanded((prev) => !prev);

		if (willCollapse && panelRef.current) {
			panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	// Omit entirely when:
	// - not enabled
	// - forbidden
	// - NoAnswer
	// - finished with no assistant HTML and no error (defensive)
	if (!enabled) {
		return null;
	}

	if (isForbidden) {
		return null;
	}

	if (hasNoAnswer) {
		return null;
	}

	const isDoneOrIdle = status === "idle" || status === "done" || status === "aborted";
	if (isDoneOrIdle && !hasAnyAssistantHtml && !error) {
		return null;
	}

	const isBusy = status === "streaming";

	// Find the last assistant message index so we can slot the form into it.
	const lastAssistantIndex = (() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			if (messages[i].role === "assistant") {
				return i;
			}
		}
		return -1;
	})();

	const secondaryForm = hasAssistantStarted ? (
		<form className="ai-chat__secondary" ref={formRef} onSubmit={handleSecondarySubmit}>
			<p><strong>Is there anything else I can help with?</strong></p>
			<textarea
				name="followUpMessage"
				value={secondaryInput}
				onChange={(e) => setSecondaryInput(e.target.value)}
				onKeyDown={handleTextareaKeyDown}
				placeholder="Continue the conversation..."
				className="ai-chat__textarea"
				rows={2}
				disabled={isBusy}
			/>
			<div className="ai-chat__actions">
				<button type="submit" className="ai-chat__button ai-chat__button--primary" disabled={isBusy}>
					Send
				</button>
			</div>
		</form>
	) : null;

	const bodyStyle = {
		maxHeight: isExpanded ? `${contentHeight}px` : `${COLLAPSED_HEIGHT}px`
	};

	const bodyClass = needsExpander && !isExpanded
		? "ai-chat__body ai-chat__body--collapsed"
		: "ai-chat__body";

	return (
		<div className="ai-chat" ref={panelRef}>
			<h2 className="ai-chat__header">AI Overview</h2>

			<div className={bodyClass} style={bodyStyle} ref={contentRef}>
				<div className="ai-chat__transcript">
					{messages.length === 0 ? (
						<div className="ai-chat__empty">Start a new conversation to see replies here.</div>
					) : (
						messages.map((m, i) => (
							<ChatBubble key={m.id} role={m.role} html={m.html} text={m.text}>
								{i === lastAssistantIndex ? secondaryForm : null}
							</ChatBubble>
						))
					)}
				</div>

				{error ? (
					<div className="ai-chat__error">
						<strong>Error:</strong> {error}
					</div>
				) : null}
			</div>

			{needsExpander ? (
				<button
					type="button"
					className="ai-chat__toggle"
					onClick={handleToggle}
				>
					{isExpanded ? "Show less" : "Show more"}
					<FontAwesomeIcon
						icon={faChevronDown}
						className={isExpanded ? "ai-chat__chev ai-chat__chev--up" : "ai-chat__chev"}
					/>
				</button>
			) : null}
		</div>
	);
}